import { useEffect, useRef } from 'react';
import { getTimer, drawPoseSkeleton } from '../../../utilis/helpers';
import { resolveFrameGeometry, SESSION_WALL_TIMER_KEY } from '../helpers';
import { usePoseWorkerStore } from '../../../zustand/pose-worker-store';
import { onEvaluationSent } from '../../../workers/pose-latency-probe';
import { poseEvaluation } from './pose-evaluation';
import { getCameraViewObjectFit } from '../../../zustand/camera-view-mode-store';
import { getIsWebRtcAudioRunning } from '../../../services/realtime-connection-store';
import {
  isFrameCollectionCurrent,
  startFrameCollection,
  stopFrameCollection,
} from './frame-collection-lifecycle';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../../../zustand/network-diagnostics-store';
// Device-performance diagnostic probe. One null check per call unless
// /debug/device-performance (or the dev-only live probe) is measuring — see
// services/device-performance/pose-probe.js for the accounting contract.
import {
  reportPoseDelivery,
  reportPoseFrame,
  reportPoseTick,
} from '../../../services/device-performance/pose-probe';

import {
  BACKOFF_DURATION_MS,
  BACKOFF_STREAK_THRESHOLD,
  DETECTION_DURATION_HISTORY_SIZE,
  FRAME_INTERVAL_MS,
  MAX_DETECTION_SIDE,
  MAX_THROTTLE_LEVEL,
  RECOVERY_DURATION_MS,
  RECOVERY_STREAK_THRESHOLD,
} from './predict-loop-config';

// How many consecutive empty MediaPipe results (person out of frame) before we
// report the person as missing — small debounce so one dropped frame doesn't
// flash the border red.
const EMPTY_STREAK_TO_MISSING = 3;

const computeDetectionInputSize = (sourceWidth, sourceHeight) => {
  const largestSide = Math.max(sourceWidth, sourceHeight);
  if (!largestSide || largestSide <= MAX_DETECTION_SIDE) {
    return { width: sourceWidth, height: sourceHeight };
  }
  const scale = MAX_DETECTION_SIDE / largestSide;
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
};

/**
 * Same external contract and behavior as usePredict (use-predecte.jsx) —
 * same WebSocket payload shape, timers, adaptive throttling, empty-frame
 * behavior, debug canvas, and effect gating — except detectForVideo runs
 * through the unified pose runtime handle from zustand/pose-worker-store.js.
 *
 * That runtime is normally a dedicated Web Worker (GPU or CPU), so a slow/first
 * inference can't freeze the page. On devices where a worker can't build the
 * MediaPipe WASM graph at all (the iOS Safari "Can't find variable: document"
 * failure), the store's fallback ladder transparently swaps in a main-thread
 * runtime instead — this hook calls runtime.detect()/runtime.warmup() either
 * way and never sees the difference. It doesn't own the runtime's lifecycle;
 * the store builds it (ensureRuntime) and this hook just drives it.
 */
const usePredictWorker = ({
  videoRef,
  responseLandMarks,
  sendJsonMessage,
  postPerformance,
  state,
  canvasRef,
  audioSectionRef,
  worker,
  time,
  isRestStage,
  isUpComingStage,
  canRunDetection = true,
  cameraAvailable = true,
  // Hard stop for both send loops (detection frames and the empty-frame
  // heartbeat): the "3, 2, 1" get-ready countdown and every pause path
  // (settings/pause modal, idle modal, backend pause stage). While true no
  // frame is captured and nothing is pushed to the socket — and because the
  // setInterval itself is torn down, nothing accumulates to burst out the
  // moment the pause modal closes.
  isFrameSendingPaused = false,
  // Hard stop for the *detection* loop alone. Deliberately narrower than
  // isFrameSendingPaused: during the "3, 2, 1" countdown we keep detecting so
  // the skeleton stays painted while the user gets into position, and only the
  // send gate in finishTick closes. Defaults to isFrameSendingPaused so a
  // caller that doesn't distinguish the two behaves exactly as before.
  isDetectionPaused = isFrameSendingPaused,
  // The pose runtime is built: the detection loop has nothing to run before it.
  modelReady = false,
  // Whether to build the pose runtime at all: the exercise page passes false until a camera is confirmed.
  needsRuntime = true,
  onFrontendFpsChange,
  // Overlay canvas (sits on top of the mirrored webcam video) that the live
  // pose skeleton is drawn onto, the color to draw it in, and a callback fired
  // when the person enters/leaves the frame.
  skeletonCanvasRef,
  skeletonColor = '#FFFFFF',
  onPersonPresenceChange,
  isStackedMobile = false,
}) => {
  const modelRef = useRef();
  const busy = useRef();
  const retryCountRef = useRef(0);
  const maxRetries = 5;
  const timeRef = useRef(time);
  const stateRef = useRef(state);
  const responseLandMarksRef = useRef(responseLandMarks);
  const postPerformanceRef = useRef(postPerformance);
  const isRestStageRef = useRef(isRestStage);
  const isUpComingStageRef = useRef(isUpComingStage);
  const cameraAvailableRef = useRef(cameraAvailable);
  const sendJsonMessageRef = useRef(sendJsonMessage);
  const detectionFrameTimestampsRef = useRef([]);
  const lastMediaPipeTimestampRef = useRef(0);
  const estimatePersonWebcamRef = useRef(null);
  const detectDurationsRef = useRef([]);
  const lastResultRef = useRef(null);
  // The result last sent with a pose (pose-evaluation.js).
  const lastSentResultRef = useRef(null);
  const tickCounterRef = useRef(0);
  const throttleLevelRef = useRef(0);
  const badStreakRef = useRef(0);
  const goodStreakRef = useRef(0);
  const skeletonColorRef = useRef(skeletonColor);
  const onPersonPresenceChangeRef = useRef(onPersonPresenceChange);
  const emptyStreakRef = useRef(0);
  const lastPresenceRef = useRef(true);
  const isFrameSendingPausedRef = useRef(isFrameSendingPaused);
  const isDetectionPausedRef = useRef(isDetectionPaused);
  const collectionIntervalRef = useRef(null);
  const collectionGenerationRef = useRef(0);
  // Separate from collectionIntervalRef: that one belongs to the detection
  // effect below, whose stopFrameCollection() runs unconditionally on every
  // re-run (even the ones that bail straight back out) — sharing a ref meant
  // that effect re-running for its own reasons (e.g. modelReady flipping true)
  // silently killed this heartbeat's interval, with nothing left to ever
  // restart it since the heartbeat effect's own deps hadn't changed.
  const heartbeatIntervalRef = useRef(null);
  // Uploaded clip vs live webcam. The detector always gets the full uncropped
  // frame now, but the *display* still differs — an uploaded clip renders
  // object-contain, the webcam object-cover — so the skeleton overlay has to be
  // projected through the matching fit. Read off state so it tracks the top-bar
  // camera/upload switch without re-creating the detect loop.
  const isUploadedVideoSourceRef = useRef(state?.mediaSourceType === 'upload');

  // --- Pose runtime plumbing ---------------------------------------------
  // The runtime is the unified handle from pose-worker-store — it may be backed
  // by a Web Worker (GPU/CPU) or by a main-thread PoseLandmarker (the iOS
  // fallback). This hook drives detect/warmup through that handle without caring
  // which rung of the fallback ladder actually won.
  // Diagnostics only — see network-diagnostics-store. Stable reference across
  // renders (zustand action), safe to close over from the effect below.
  const recordNetworkMetric = useNetworkDiagnostics(state => state.record);

  const poseRuntime = usePoseWorkerStore(state => state.runtime);
  const poseRuntimeError = usePoseWorkerStore(state => state.error);
  const ensureRuntime = usePoseWorkerStore(state => state.ensureRuntime);
  const releaseSessionRuntime = usePoseWorkerStore(state => state.releaseSessionRuntime);
  const runtimeRef = useRef(poseRuntime);
  const workerReadyRef = useRef(modelReady);
  const warmedUpRef = useRef(false);
  const inflightRef = useRef(false); // one detect request outstanding at a time

  const recordFrontendFpsSample = timestampMs => {
    const recentTimestamps = detectionFrameTimestampsRef.current.filter(
      value => timestampMs - value < 1000
    );
    recentTimestamps.push(timestampMs);
    detectionFrameTimestampsRef.current = recentTimestamps;
    return recentTimestamps.length;
  };

  const resolveNextMediaPipeTimestamp = () => {
    const now = Math.ceil(performance.now());
    const nextTimestamp = Math.max(now, lastMediaPipeTimestampRef.current + 1);
    lastMediaPipeTimestampRef.current = nextTimestamp;
    return nextTimestamp;
  };

  const recordDetectionDuration = durationMs => {
    const durations = detectDurationsRef.current;
    durations.push(durationMs);
    if (durations.length > DETECTION_DURATION_HISTORY_SIZE) {
      durations.shift();
    }
    const averageDurationMs = durations.reduce((sum, value) => sum + value, 0) / durations.length;

    if (averageDurationMs > BACKOFF_DURATION_MS) {
      badStreakRef.current += 1;
      goodStreakRef.current = 0;
      if (badStreakRef.current >= BACKOFF_STREAK_THRESHOLD) {
        throttleLevelRef.current = Math.min(MAX_THROTTLE_LEVEL, throttleLevelRef.current + 1);
        badStreakRef.current = 0;
      }
    } else if (averageDurationMs < RECOVERY_DURATION_MS) {
      goodStreakRef.current += 1;
      badStreakRef.current = 0;
      if (goodStreakRef.current >= RECOVERY_STREAK_THRESHOLD) {
        throttleLevelRef.current = Math.max(0, throttleLevelRef.current - 1);
        goodStreakRef.current = 0;
      }
    } else {
      badStreakRef.current = 0;
      goodStreakRef.current = 0;
    }
  };

  const shouldSkipDetectionThisTick = () => {
    tickCounterRef.current += 1;
    if (!lastResultRef.current) {
      return false;
    }

    const throttleLevel = throttleLevelRef.current;
    if (throttleLevel === 0) {
      return false;
    }
    if (throttleLevel === 1) {
      return tickCounterRef.current % 2 === 1;
    }
    return tickCounterRef.current % 3 !== 0;
  };

  useEffect(() => {
    modelRef.current = worker;
    busy.current = false;
  }, [worker]);

  useEffect(() => {
    timeRef.current = time;
    stateRef.current = state;
    isUploadedVideoSourceRef.current = state?.mediaSourceType === 'upload';
    responseLandMarksRef.current = responseLandMarks;
    postPerformanceRef.current = postPerformance;
    isRestStageRef.current = isRestStage;
    isUpComingStageRef.current = isUpComingStage;
    cameraAvailableRef.current = cameraAvailable;
    // Diagnostics only: measures the serialized size of what's about to be
    // sent, then calls straight through to the real sendJsonMessage. Does not
    // change the payload or the send itself — a second, throwaway
    // JSON.stringify purely for its .length, since react-use-websocket does
    // its own serialization internally and exposes no hook into that size.
    sendJsonMessageRef.current = payload => {
      try {
        recordNetworkMetric(
          'websocket',
          'outbound_message_bytes',
          JSON.stringify(payload).length,
          'bytes'
        );
      } catch (_error) {
        // Never let instrumentation block a real send.
      }
      return sendJsonMessage(payload);
    };
    skeletonColorRef.current = skeletonColor;
    onPersonPresenceChangeRef.current = onPersonPresenceChange;
  }, [
    canRunDetection,
    cameraAvailable,
    recordNetworkMetric,
    isRestStage,
    isUpComingStage,
    postPerformance,
    responseLandMarks,
    sendJsonMessage,
    state,
    time,
    skeletonColor,
    onPersonPresenceChange,
  ]);

  useEffect(() => {
    workerReadyRef.current = modelReady;
  }, [modelReady]);

  // Render-time refs close the gap before effect cleanup clears the interval.
  isFrameSendingPausedRef.current = isFrameSendingPaused;
  isDetectionPausedRef.current = isDetectionPaused;

  useEffect(() => {
    if (poseRuntimeError) {
      console.error('MediaPipe pose runtime failed to initialize.', poseRuntimeError);
    }
  }, [poseRuntimeError]);

  useEffect(() => {
    lastMediaPipeTimestampRef.current = 0;
  }, [modelReady]);

  // Finishes a tick started either synchronously (skip path) or by a worker
  // 'result'/'dropped' message — shared so both paths produce byte-identical
  // WebSocket payloads and debug-canvas behavior.
  const finishTick = ({
    result,
    dataThatSendToBackend,
    startTimeMs,
    detectionSource,
    collectionGeneration,
    // Probe only: whether `result` is a new inference (vs. a reused/empty one).
    fresh = false,
  }) => {
    if (
      isDetectionPausedRef.current ||
      !isFrameCollectionCurrent(collectionGenerationRef, collectionGeneration)
    ) {
      return;
    }
    const deliveryStartedAt = performance.now();
    deliverFrame({ result, dataThatSendToBackend, startTimeMs, detectionSource });
    reportPoseDelivery({ fresh, startedAt: deliveryStartedAt, endedAt: performance.now() });
  };

  const deliverFrame = ({ result, dataThatSendToBackend, startTimeMs, detectionSource }) => {
    const frontendFps = recordFrontendFpsSample(startTimeMs);
    if (typeof onFrontendFpsChange === 'function') {
      onFrontendFpsChange(frontendFps);
    }

    // Live skeleton overlay on top of the webcam + person-in-frame tracking.
    const poseLandmarks = result?.landmarks?.[0];
    const hasPose = Array.isArray(poseLandmarks) && poseLandmarks.length > 0;

    if (skeletonCanvasRef?.current) {
      // Landmarks are normalized over the full uncropped video frame, so the
      // overlay has to re-apply whatever object-fit the video is displayed
      // with — otherwise the skeleton drifts off the body by exactly the
      // amount the browser crops (cover) or letterboxes (contain).
      drawPoseSkeleton({
        canvasElement: skeletonCanvasRef.current,
        landmarks: hasPose ? poseLandmarks : null,
        color: skeletonColorRef.current,
        sourceWidth: detectionSource?.videoWidth || detectionSource?.width,
        sourceHeight: detectionSource?.videoHeight || detectionSource?.height,
        // The webcam's fit is a user setting now (pause modal - camera view), so
        // it is read per frame rather than assumed to be `cover`. An uploaded clip
        // is always shown whole, and that setting does not apply to it.
        objectFit: isUploadedVideoSourceRef.current ? 'contain' : getCameraViewObjectFit(),
      });
    }

    // MediaPipe returning an empty array means the person left the frame — debounce
    // by a short empty streak so a single dropped frame doesn't flip the border.
    if (hasPose) {
      emptyStreakRef.current = 0;
    } else {
      emptyStreakRef.current += 1;
    }
    const present = emptyStreakRef.current < EMPTY_STREAK_TO_MISSING;
    if (present !== lastPresenceRef.current) {
      lastPresenceRef.current = present;
      onPersonPresenceChangeRef.current?.(present);
    }

    // A detect started before the pause can resolve after it — the skeleton /
    // presence work above is local and harmless, but the frame itself must not
    // reach the backend, otherwise a stale frame lands right as the pause
    // modal opens (or during the get-ready countdown).
    if (isFrameSendingPausedRef.current) {
      localStorage.removeItem('isPlaySound');
      return;
    }

    // Whether the voice agent is audibly speaking right now. Read synchronously
    // through the store's getState() — no hooks, no re-render, safe to call on
    // every frame from inside this loop. The backend uses it to avoid stacking
    // a new spoken cue on top of one the user is still hearing.
    // A remote pose carries its own time (the pose service's presentation clock, seconds). It goes
    // with the pose on every tick that carries it, re-sends included: the backend's rotation matcher
    // takes one stream token per distinct stamp, so a repeated result makes none and the tokens are
    // spaced as the camera's frames were, not as the ticks that relayed them.
    let poseTime = null;
    if (typeof result?.remote?.frameT === 'number') {
      poseTime = result.remote.frameT;
    } else if (typeof result?.remote?.timestampMs === 'number') {
      poseTime = result.remote.timestampMs / 1000;
    }
    const evaluationPayload = {
      ...dataThatSendToBackend,
      frontendFps,
      isAudioRunning: getIsWebRtcAudioRunning(),
      ...(poseTime === null ? {} : { poseTime }),
    };

    if (result?.landmarks && result?.landmarks?.length > 0) {
      if (!postPerformanceRef.current) {
        // The backend echoes `id` on its reply: the probe times the round trip (LiveKit only).
        const id = onEvaluationSent(result.remote);
        sendJsonMessageRef.current({
          evaluation: poseEvaluation(result, lastSentResultRef.current, evaluationPayload),
          ...(id ? { id } : {}),
        });
        lastSentResultRef.current = result;
      }
    } else if (result?.landmarks?.length === 0) {
      sendJsonMessageRef.current({
        evaluation: {
          poseLandmarks: [],
          ...evaluationPayload,
        },
      });
    } else {
      sendJsonMessageRef.current({
        evaluation: {
          poseLandmarks: [],
          ...evaluationPayload,
        },
      });
    }
    localStorage.removeItem('isPlaySound');
  };

  // Built only once there is a camera to read (idempotent, so a camera that comes back reuses it).
  useEffect(() => {
    if (needsRuntime) {
      ensureRuntime();
    }
  }, [needsRuntime, ensureRuntime]);
  useEffect(() => releaseSessionRuntime, [releaseSessionRuntime]);

  // Keep a ref to the live runtime handle so the detect/warmup loop (which runs
  // off a setInterval closure, not the render cycle) always sees the current one.
  useEffect(() => {
    runtimeRef.current = poseRuntime;
    return () => {
      warmedUpRef.current = false;
      inflightRef.current = false;
    };
  }, [poseRuntime]);

  // Warm-up: fires the worker's first (slow) detectForVideo call as soon as
  // the worker and a real video frame are ready, independent of
  // canRunDetection/cameraAvailable/state.stopSend. Its result is discarded —
  // it never touches busy/inflightRef/lastResultRef/FPS/sendJsonMessage.
  useEffect(() => {
    if (warmedUpRef.current) {
      return undefined;
    }

    let cancelled = false;
    let rafId = null;

    const tryWarmUp = async () => {
      if (cancelled) {
        return;
      }
      const videoElement = videoRef?.current?.video;
      if (
        !workerReadyRef.current ||
        !runtimeRef.current ||
        !videoElement ||
        videoElement.readyState < 2
      ) {
        rafId = requestAnimationFrame(tryWarmUp);
        return;
      }

      warmedUpRef.current = true;
      try {
        const side = MAX_DETECTION_SIDE;
        const ratio = (videoElement.videoHeight || 9) / (videoElement.videoWidth || 16);
        const bitmap = await createImageBitmap(videoElement, {
          resizeWidth: side,
          resizeHeight: Math.max(1, Math.round(side * ratio)),
        });
        // The remote runtime streams the video element's own source (an uploaded clip has
        // no camera stream); the MediaPipe runtimes have no such method.
        runtimeRef.current?.attachSource?.(videoElement);
        runtimeRef.current?.warmup({ bitmap, timestampMs: performance.now() });
      } catch (error) {
        console.warn('MediaPipe warm-up failed (non-fatal).', error);
      }
    };

    rafId = requestAnimationFrame(tryWarmUp);

    return () => {
      cancelled = true;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [videoRef]);

  const estimatePersonWebcam = async () => {
    const collectionGeneration = collectionGenerationRef.current;
    if (isDetectionPausedRef.current) {
      reportPoseTick('not-ready', { reason: 'detection-paused' });
      return;
    }
    const videoElement = videoRef?.current?.video;

    if (!videoElement) {
      reportPoseTick('not-ready', { reason: 'no-video-element' });
      console.warn('Video element not found');
      return;
    }

    if (!workerReadyRef.current) {
      reportPoseTick('not-ready', { reason: 'model-not-ready' });
      return;
    }

    if (videoElement.readyState < 2) {
      reportPoseTick('not-ready', { reason: 'video-not-ready' });
      retryCountRef.current += 1;
      if (retryCountRef.current <= maxRetries) {
        console.log(`Video not ready, retrying... (${retryCountRef.current}/${maxRetries})`);
        setTimeout(() => {
          if (
            isFrameCollectionCurrent(collectionGenerationRef, collectionGeneration) &&
            !isDetectionPausedRef.current &&
            stateRef.current.cameraIsActive &&
            stateRef.current.stopSend
          ) {
            estimatePersonWebcam();
          }
        }, 200);
      } else {
        console.error('Video is not ready for rendering after max retries.');
        retryCountRef.current = 0;
      }
      return;
    }

    retryCountRef.current = 0;

    // Don't start a second capture/detect while one is already in flight in
    // the worker — mirrors the old single-threaded busy.current guard.
    if (inflightRef.current) {
      reportPoseTick('not-ready', { reason: 'inference-in-flight' });
      return;
    }

    const startTimeMs = resolveNextMediaPipeTimestamp();
    const isPlaySound = localStorage.getItem('isPlaySound');

    const dataThatSendToBackend = {
      recordTimer: getTimer({ key: 'recordTimer', timer: timeRef.current }) ?? 0,
      isAudioFinished: JSON.parse(isPlaySound),
      eft: getTimer({ key: 'eft-time', timer: timeRef.current }) ?? 0,
      // Wall time for this socket — unlike `eft` it does not stop when the backend
      // freezes the set on quality, which is what lets the backend cap a hold the
      // patient cannot complete. Held only by a genuine suspension.
      wallEft: getTimer({ key: SESSION_WALL_TIMER_KEY, timer: timeRef.current }) ?? 0,
      // frameWidth / frameHeight: the pixel geometry the landmarks on this frame
      // are normalized against. Deliberately the *source* video dimensions, not
      // the (possibly downscaled, MAX_DETECTION_SIDE) bitmap handed to the
      // runtime — landmarks come back normalized 0-1 over the full frame either
      // way, so the source frame is what those coordinates map onto. Read per
      // frame rather than once at connect, so a resolution change (device swap,
      // camera ↔ uploaded clip) is reported instead of going stale.
      ...resolveFrameGeometry(videoRef),
    };

    if (!videoElement || videoElement.readyState < 2) {
      reportPoseTick('not-ready', { reason: 'video-not-ready' });
      console.error('Video is not ready for rendering.');
      return;
    }

    // No crop/letterbox stage — the raw video frame goes to MediaPipe whole, so
    // landmarks are normalized against the full source frame and nothing at the
    // edges is ever cut off.
    const detectionSource = videoElement;

    if (shouldSkipDetectionThisTick()) {
      reportPoseTick('throttled', { throttleLevel: throttleLevelRef.current });
      // No round trip needed — same as before, purely synchronous.
      finishTick({
        result: lastResultRef.current,
        dataThatSendToBackend,
        startTimeMs,
        detectionSource,
        collectionGeneration,
      });
      return;
    }

    const { width: inputWidth, height: inputHeight } = computeDetectionInputSize(
      videoElement.videoWidth,
      videoElement.videoHeight
    );

    const runtime = runtimeRef.current;
    if (!runtime) {
      reportPoseTick('not-ready', { reason: 'no-runtime' });
      return;
    }

    reportPoseTick('admitted', { throttleLevel: throttleLevelRef.current });
    const frameStartedAt = performance.now();
    let bitmap;
    try {
      bitmap = await createImageBitmap(detectionSource, {
        resizeWidth: inputWidth,
        resizeHeight: inputHeight,
      });
    } catch (error) {
      reportPoseFrame({
        outcome: 'capture-error',
        admittedAt: frameStartedAt,
        captureEndedAt: null,
        resultAt: performance.now(),
      });
      console.error('Failed to capture frame for MediaPipe detection.', error);
      finishTick({
        result: { landmarks: [], worldLandmarks: [] },
        dataThatSendToBackend,
        startTimeMs,
        detectionSource,
        collectionGeneration,
      });
      return;
    }

    // The runtime owns the bitmap's lifecycle from here (transfers it to the
    // worker, or draws + closes it on the main thread) — don't touch it after.
    const captureEndedAt = performance.now();
    inflightRef.current = true;
    let result;
    let fresh = false;
    try {
      const detectResult = await runtime.detect({
        bitmap,
        timestampMs: startTimeMs,
        inputWidth,
        inputHeight,
      });

      reportPoseFrame({
        outcome: detectResult?.dropped ? 'dropped' : 'completed',
        admittedAt: frameStartedAt,
        captureEndedAt,
        resultAt: performance.now(),
        inferenceMs: detectResult?.durationMs ?? null,
        timing: detectResult?.timing ?? null,
        hasPose: Boolean(detectResult?.landmarks?.length),
      });

      if (
        isDetectionPausedRef.current ||
        !isFrameCollectionCurrent(collectionGenerationRef, collectionGeneration)
      ) {
        return;
      }

      if (detectResult?.dropped) {
        // Runtime was still busy (rare — inflightRef gates against it) — reuse
        // the last known result like a skipped tick.
        result = lastResultRef.current;
      } else {
        // The backoff protects the main thread from a slow local model. A remote result cost it
        // nothing, and its `durationMs` is the server's inference time: fed to the backoff it throttled
        // the remote path to every third tick (found 2026-10-18).
        if (runtime.kind !== 'remote') {
          recordDetectionDuration(detectResult.durationMs);
        }
        result = {
          landmarks: detectResult.landmarks,
          worldLandmarks: detectResult.worldLandmarks,
          // Remote runtime only: the model's own output, relayed to the backend as-is.
          mhr: detectResult.mhr ?? null,
          remote: detectResult.remote ?? null,
        };
        lastResultRef.current = result;
        fresh = true;
      }
    } catch (error) {
      reportPoseFrame({
        outcome: 'detect-error',
        admittedAt: frameStartedAt,
        captureEndedAt,
        resultAt: performance.now(),
      });
      console.error('MediaPipe pose detection failed.', error);
      result = { landmarks: [], worldLandmarks: [] };
    } finally {
      inflightRef.current = false;
    }

    finishTick({
      result,
      dataThatSendToBackend,
      startTimeMs,
      detectionSource,
      collectionGeneration,
      fresh,
    });
  };

  estimatePersonWebcamRef.current = estimatePersonWebcam;

  useEffect(() => {
    stopFrameCollection({
      intervalRef: collectionIntervalRef,
      generationRef: collectionGenerationRef,
      discard: () => {
        lastResultRef.current = null;
      },
    });

    if (!modelReady || !state.stopSend || !cameraAvailable || isDetectionPaused) {
      return undefined;
    }

    detectDurationsRef.current = [];
    lastResultRef.current = null;
    tickCounterRef.current = 0;
    throttleLevelRef.current = 0;
    badStreakRef.current = 0;
    goodStreakRef.current = 0;

    startFrameCollection({
      intervalRef: collectionIntervalRef,
      generationRef: collectionGenerationRef,
      intervalMs: FRAME_INTERVAL_MS,
      tick: () => {
        if (busy.current) {
          reportPoseTick('busy');
          return;
        }

        busy.current = true;
        Promise.resolve(estimatePersonWebcamRef.current()).finally(() => {
          busy.current = false;
        });
        localStorage.removeItem('isPlaySound');
      },
    });

    return () => {
      stopFrameCollection({
        intervalRef: collectionIntervalRef,
        generationRef: collectionGenerationRef,
        discard: () => {
          lastResultRef.current = null;
        },
      });
      // A tick can be mid-flight when the pause lands. Releasing these guards
      // means the loop restarts clean instead of staying wedged on a `busy`
      // flag left true by a tick whose `finally` runs after this teardown —
      // and any frame that in-flight tick still resolves is dropped by the
      // send gate in finishTick rather than queued up for after the pause.
      busy.current = false;
      inflightRef.current = false;
    };
    // Depend only on what actually gates/starts the loop. `canRunDetection` and
    // `state.cameraIsActive` were never read in this effect (the gate is
    // modelReady && state.stopSend && cameraAvailable, and camera-active state is
    // read live via stateRef inside the loop), yet they change on every stage
    // flip — which tore down and recreated the interval constantly, resetting
    // the throttle refs and clustering sends around transitions.
  }, [cameraAvailable, modelReady, state.stopSend, isDetectionPaused]);

  useEffect(() => {
    if (!state.stopSend || cameraAvailable || isFrameSendingPaused) {
      return undefined;
    }
    const startTimeMs = resolveNextMediaPipeTimestamp();

    let frontendFps = 0;
    frontendFps = recordFrontendFpsSample(startTimeMs);
    if (typeof onFrontendFpsChange === 'function') {
      onFrontendFpsChange(frontendFps);
    }
    heartbeatIntervalRef.current = setInterval(() => {
      if (isFrameSendingPausedRef.current) {
        return;
      }
      sendJsonMessageRef.current({
        evaluation: {
          poseLandmarks: [],
          poseWorldLandmarks: [],
          eft: getTimer({ key: 'eft-time', timer: timeRef.current }) ?? 0,
          // Same clock as the detection path: the backend needs it on every frame
          // it evaluates, and this heartbeat is the only frame an offline set has.
          wallEft: getTimer({ key: SESSION_WALL_TIMER_KEY, timer: timeRef.current }) ?? 0,
          isAudioFinished: true,
          // Same keys as the detection path, for the same payload-shape reason.
          // This branch runs with no detection behind it, so the video element
          // is often absent entirely — resolveFrameGeometry then contributes
          // nothing and the backend keeps its defaults.
          ...resolveFrameGeometry(videoRef),
        },
      });
      localStorage.removeItem('isPlaySound');
    }, FRAME_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
    // Same reasoning as the detection interval above: only cameraAvailable and
    // state.stopSend actually gate this empty-frame heartbeat. `videoRef` is a
    // ref object with a stable identity, so listing it doesn't churn the
    // interval — it's read live inside the tick.
  }, [cameraAvailable, state.stopSend, isFrameSendingPaused, videoRef]);
};

export default usePredictWorker;
